import { useState, type FormEvent } from "react";
import { Navigate, useLocation } from "react-router-dom";

import BrandLogo from "@/components/feature/BrandLogo";
import { useAuth } from "@/hooks/useAuth";

export default function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const location = useLocation();

  if (isAuthenticated) {
    const from = (location.state as { from?: string })?.from || "/";
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");

    if (!username.trim() || !password.trim()) {
      setError("Введите логин и пароль");
      return;
    }

    setLoading(true);
    const result = await login(username.trim(), password.trim());
    setLoading(false);

    if (result.error) {
      setError(result.error);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-bg px-4 py-10">
      <div
        className="pointer-events-none absolute inset-0 opacity-50"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 20%, rgba(217,119,6,.08), transparent 24%), radial-gradient(circle at 80% 10%, rgba(180,83,9,.06), transparent 20%), linear-gradient(rgba(250,250,248,.86), rgba(250,250,248,.96))",
        }}
      />

      <div className="relative mx-auto flex min-h-[calc(100vh-5rem)] w-full max-w-5xl items-center justify-center">
        <div className="grid w-full items-center gap-10 lg:grid-cols-[1.1fr_420px]">
          <div className="hidden lg:block">
            <div className="max-w-xl">
              <div className="mb-6 inline-flex items-center gap-3 rounded-full border border-border bg-panel px-4 py-2 text-sm font-medium text-ink-3 shadow-ds-1">
                <BrandLogo size={28} />
                <span>Тёплая CRM для каталога, заказов и ежедневной работы</span>
              </div>

              <h1 className="text-4xl font-bold leading-tight tracking-[-0.03em] text-ink">
                Спокойный рабочий интерфейс для команды, которая ведёт товары и заказы каждый день.
              </h1>

              <p className="mt-5 max-w-lg text-base leading-7 text-ink-3">
                Держите под рукой каталог, заказы и ключевые показатели магазина в одном аккуратном пространстве.
              </p>

              <div className="mt-8 grid gap-3 sm:grid-cols-3">
                {[
                  ["Каталог", "Товары и SKU в одном месте"],
                  ["Заказы", "Фильтры, детали и статусы"],
                  ["Аналитика", "Показатели по продажам и трафику"],
                ].map(([title, body]) => (
                  <div key={title} className="panel-soft p-4">
                    <p className="text-sm font-semibold text-ink">{title}</p>
                    <p className="mt-1 text-sm leading-6 text-ink-4">{body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="w-full max-w-md justify-self-center">
            <div className="panel overflow-hidden">
              <div className="border-b border-border-soft bg-panel-2 px-8 py-7">
                <div className="flex items-center gap-3">
                  <BrandLogo size={40} />
                  <div>
                    <p className="text-xl font-bold tracking-[-0.02em] text-ink">Пчелиная CRM</p>
                    <p className="mt-1 text-sm text-ink-4">Вход для сотрудников магазина</p>
                  </div>
                </div>
              </div>

              <div className="px-8 py-8">
                <h2 className="text-lg font-semibold text-ink">Вход в систему</h2>
                <p className="mt-1 text-sm text-ink-4">Введите логин и пароль, чтобы перейти в рабочее пространство.</p>

                <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-ink-3">Логин</label>
                    <input
                      type="text"
                      value={username}
                      onChange={(event) => setUsername(event.target.value)}
                      placeholder="admin"
                      className="soft-input placeholder:text-ink-mute"
                      autoComplete="username"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-ink-3">Пароль</label>
                    <input
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      placeholder="••••••••"
                      className="soft-input placeholder:text-ink-mute"
                      autoComplete="current-password"
                    />
                  </div>

                  {error ? (
                    <div className="flex items-start gap-2 rounded-xl border border-danger-border bg-danger-bg p-3">
                      <i className="ri-error-warning-line mt-0.5 shrink-0 text-sm text-danger" />
                      <span className="text-sm text-danger">{error}</span>
                    </div>
                  ) : null}

                  <button type="submit" disabled={loading} className="btn-primary w-full">
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin text-base" />
                        Вход...
                      </span>
                    ) : (
                      <>
                        <i className="ri-login-box-line" />
                        Войти
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

            <p className="mt-4 text-center text-xs text-ink-mute">Пчелиная CRM для ежедневной работы с товарами и заказами</p>
          </div>
        </div>
      </div>
    </div>
  );
}
